<script lang="ts">
  import type { HelpDialogSection } from "../../../../../../core/help/types"
  import type { Snippet } from "svelte"
  import { PageTabs } from "../../../../../_shared/_modules/page-tabs/index"
  import SafeHtml from "../../../../../security/_modules/safe-html/index"

  const {
    sections,
    children,
  }: {
    sections: (HelpDialogSection & { id: number })[]
    children: Snippet<[{ navigation: Snippet; content: Snippet; contentKey: number | undefined }]>
  } = $props()
  const instanceId = $props.id()
  let selectedId = $state<number | null>(null)
  const activeSection = $derived(
    sections.find((section) => section.id === selectedId) ?? sections[0],
  )
  const hasTabs = $derived(sections.length > 1)
</script>

{#snippet navigation()}
  {#if hasTabs}
    <PageTabs
      mode="tabs"
      label="Hilfethemen"
      items={sections.map((section) => ({
        id: `${section.id}`,
        label: section.title,
        icon: section.icon,
      }))}
      activeId={`${activeSection.id}`}
      onSelect={(id) => (selectedId = Number(id))}
      idPrefix={instanceId}
      panelId={`${instanceId}-panel`}
    />
  {/if}
{/snippet}

{#snippet content()}
  <div class={["help-topics", { "has-tabs": hasTabs }]}>
    {#if activeSection}
      {#key activeSection.id}
        {#snippet topicContent()}
          <h3 id={`${instanceId}-heading`}>{activeSection.title}</h3>
          <div class={["help-topics__layout", { illustrated: !!activeSection.image }]}>
            <div class="help-topics__copy">
              {#each activeSection.items as item, index (index)}
                <div class="help-topics__paragraph"><SafeHtml html={item.text} /></div>
              {/each}
            </div>
            {#if activeSection.image}
              <figure>
                <a
                  class="help-topics__image-link"
                  href={activeSection.image.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${activeSection.title}: Bild in voller Größe öffnen`}
                >
                  <img src={activeSection.image.src} alt={activeSection.image.alt} />
                  <span>Bild in voller Größe öffnen</span>
                </a>
                {#if activeSection.image.caption}
                  <figcaption>{activeSection.image.caption}</figcaption>
                {/if}
              </figure>
            {/if}
          </div>
        {/snippet}
        {#if hasTabs}
          <div
            class="help-topics__panel"
            id={`${instanceId}-panel`}
            role="tabpanel"
            aria-labelledby={`${instanceId}-tab-${activeSection.id}`}
            tabindex="0"
          >
            {@render topicContent()}
          </div>
        {:else}
          <section
            class="help-topics__panel"
            id={`${instanceId}-panel`}
            aria-labelledby={`${instanceId}-heading`}
          >
            {@render topicContent()}
          </section>
        {/if}
      {/key}
    {/if}
  </div>
{/snippet}

{@render children({ navigation, content, contentKey: activeSection?.id })}

<style lang="less">
  .help-topics {
    min-width: 0;
    color: var(--text-invers-100) !important;
  }

  .help-topics__panel {
    padding: 0;
    min-width: 0;
    overflow-wrap: anywhere;

    h3 {
      margin: 0 0 0.9rem;
      color: inherit;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.4;
    }
  }

  .has-tabs .help-topics__panel {
    min-height: 14rem;
  }

  .help-topics__layout {
    display: grid;
    gap: 24px;
    align-items: start;
    min-width: 0;
  }

  .help-topics__layout.illustrated {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  }

  .help-topics__copy {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    max-width: 70ch;
    min-width: 0;
    color: var(--text-invers-100) !important;
    font-size: 16px;
    line-height: 1.6;
  }

  .help-topics__paragraph,
  .help-topics__paragraph :global(*) {
    font-size: inherit;
    line-height: inherit;
  }

  .help-topics__paragraph :global(h1),
  .help-topics__paragraph :global(h2),
  .help-topics__paragraph :global(h3) {
    color: inherit;
  }

  .help-topics__paragraph :global(p) {
    margin: 0 0 0.75em;
  }

  .help-topics__paragraph :global(p),
  .help-topics__paragraph :global(li),
  .help-topics__paragraph :global(strong) {
    color: var(--text-invers-100) !important;
    font-size: inherit;
    line-height: inherit;
  }

  .help-topics__paragraph :global(p:last-child) {
    margin-bottom: 0;
  }

  .help-topics__paragraph :global(ul),
  .help-topics__paragraph :global(ol) {
    padding-inline-start: 1.4em;
  }

  .help-topics__paragraph :global(ul) {
    list-style-type: disc;
  }

  .help-topics__paragraph :global(ol) {
    list-style-type: decimal;
  }

  .help-topics__paragraph :global(a) {
    color: var(--text-invers-100) !important;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .help-topics__paragraph :global(li) {
    display: list-item;
    margin-block: 0.35em;
    line-height: inherit;
  }

  figure {
    min-width: 0;
    margin: 0;

    img {
      display: block;
      width: 100%;
      height: auto;
      max-height: clamp(180px, calc(80dvh - 330px), 370px);
      object-fit: contain;
      margin-inline: auto;
    }

    figcaption {
      margin-top: 0.6rem;
      color: var(--text-invers-100) !important;
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .help-topics__image-link {
    display: grid;
    gap: 10px;
    color: var(--text-invers-100) !important;
    font-size: 14px;
    line-height: 1.5;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .help-topics__image-link:focus-visible {
    outline: 2px solid var(--primary-100);
    outline-offset: 4px;
  }

  .help-topics__panel:focus-visible {
    outline: 2px solid var(--primary-100);
    outline-offset: -2px;
  }

  @media (max-width: 900px) {
    .help-topics__layout.illustrated {
      grid-template-columns: minmax(0, 1fr);
    }

    .has-tabs .help-topics__panel {
      min-height: 10rem;
    }
  }
</style>
